import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, NgZone, AfterViewInit, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import type * as am5 from '@amcharts/amcharts5';
import type * as am5xy from '@amcharts/amcharts5/xy';
import { SensorRecordService } from 'src/app/services/sensorRecord.service';
import { ToastService } from 'src/app/services/toast.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { AuthService } from 'src/app/services';
import { ChartService } from 'src/app/services/chart.service';
import { SensorService } from 'src/app/services/sensor.service';
import { Sensor } from 'src/app/models/sensor.model';
import { forkJoin, Observable } from 'rxjs';
import { Enum } from 'src/app/models/enum.model';
import { TimeIntervalEnum } from 'src/app/enums/timeInterval.enum';
import { MatDialog } from '@angular/material/dialog';
import { DateRange } from 'src/app/models/dateRange.model';
import { DataAnalysisRequestModel } from 'src/app/models/dataAnalysisRequest.model';
import { SensorAnalysisRecord } from 'src/app/models/sensorAnalysisRecord.model';
import { UserPreferences } from 'src/app/models/userPreferences.model';
import { DataAnalysisExportModel } from 'src/app/models/dataAnalysisExport.model';
import { DeviceValue } from 'src/app/models/deviceValue.model';
import { OnZoom } from '../chartBase/onZoom.interface';
import { SensorRecord } from 'src/app/models/sensorRecord.model';
import { ResizedEvent } from 'angular-resize-event-package';
import { TimeZoneService } from 'src/app/services/timeZone.service';
import { Lookup } from 'src/app/models/lookup.model';
import { CustomEntityService } from 'src/app/services/customEntity.service';
import { EndPointSensorLookup } from 'src/app/models/endPointSensorLookup.model';
import { TimeRangeDialogComponent } from 'src/app/modals/timeRangeDialog/timeRangeDialog.component';
import { ValueConversionService } from 'src/app/services/value-conversion.service';

@Component({
  standalone: false,
    selector: 'app-data-analysis-page',
    templateUrl: 'dataAnalysisPage.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['dataAnalysisPage.component.scss'],
})
export class DataAnalysisPageComponent implements OnInit, AfterViewInit, OnDestroy, OnZoom {

    readonly CHART_DIV_ID = 'data-analysis-chart';

    endPoints: EndPointSensorLookup[];
    sensors: EndPointSensorLookup[];
    selected: string[];
    isLoading = false;
    chart: am5xy.XYChart;
    fromDate: Date;
    toDate: Date;
    timeZone: string;
    timeZoneLabel: string;
    parameters: Enum[];
    selectedParams: number[];
    timeIntervals: Enum[];
    timeIntervalId: number;
    shiftInterval: DateRange;
    preferences: UserPreferences;
    deviceValues: { [key: string]: DeviceValue | undefined };
    rowDevices: string[];
    columnParameters: number[];
    paramNamesById = new Map<number, string>();
    paramUnitsById = new Map<number, string>();
    deviceAverages: Record<string, Record<number, number>> = {};
    devicePeaks: Record<string, Record<number, number>> = {};
    disposer: am5.IDisposer;
    initDisposer: am5.IDisposer;
    analyticsDisposer: am5.IDisposer;
    shouldRecordAnalyticsZoom = false;
    analyticsVerticalDisposer: am5.IDisposer;
    shouldRecordVerticalZoom = false;

    constructor(private zone: NgZone,
        private sensorService: SensorService,
        private customEntityService: CustomEntityService,
        private sensorRecordService: SensorRecordService,
        private toastService: ToastService,
        private dateFormatService: DateFormatService,
        private chartService: ChartService,
        private valueConversionService: ValueConversionService,
        private dialog: MatDialog,
        authService: AuthService,
        timeZoneService: TimeZoneService) {

        const currentUser = authService.getCurrentUser();
        this.timeZone = currentUser.userTimeZone;
        this.timeZoneLabel = timeZoneService.createTimeZoneLabel(this.timeZone);
        this.preferences = currentUser.preferences;
        this.shiftInterval = new DateRange(undefined, undefined);
        this.parameters = this.chartService.getParameters();
        this.paramNamesById = new Map(this.parameters.map(p => [p.id, this.chartService.getParameterName(p.id)]));
        this.paramUnitsById = new Map(this.parameters.map(p => [p.id, this.valueConversionService.getParameterUnit(p.id)]));
        this.timeIntervals = [
            new Enum(TimeIntervalEnum.None, 'None'),
            new Enum(TimeIntervalEnum.Sliding15Minute, '15 min STEL (moving)'),
            new Enum(TimeIntervalEnum.Sliding1Hour, '1h Moving Average'),
            new Enum(TimeIntervalEnum.Sliding4Hour, '4h Moving Average'),
            new Enum(TimeIntervalEnum.Sliding8Hour, '8h Moving Average'),
            new Enum(TimeIntervalEnum.Hourly, '1h Fixed Average'),
            new Enum(TimeIntervalEnum.Daily, 'Daily (24h average)'),
            new Enum(TimeIntervalEnum.DailyShift, 'Daily Shifts TWA')
        ];
        this.deviceValues = {};
    }

    ngOnInit() {
        this.isLoading = true;
        this.selectedParams = this.chartService.getDefaultParameters();
        this.selected = this.preferences.selectedDevices;
        this.timeIntervalId = this.preferences.timeIntervalId;
        this.fromDate = this.preferences.fromDate;
        this.toDate = this.preferences.toDate;
        this.shiftInterval = new DateRange(this.preferences.shiftStart, this.preferences.shiftEnd);
        forkJoin({
            sensors: this.sensorService.getSensorLookupList(),
            endPoints: this.customEntityService.getEndPointLookupList()
        }).subscribe(
            result => {
                this.mapToEndPointsAndSensors(result.sensors, result.endPoints);
                this.isLoading = false;
            },
            error => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        );
    }

    mapToEndPointsAndSensors(sensors: Sensor[], endPoints: Lookup[]) {
        this.endPoints = [];
        this.sensors = [];
        endPoints.forEach(endPoint =>
            this.endPoints.push(new EndPointSensorLookup(
                `0+${endPoint.id}`,
                endPoint.name,
                String(endPoint.id),
                null,
                true
            )));
        sensors.forEach(sensor =>
            this.sensors.push(new EndPointSensorLookup(
                `1+${sensor.id}`,
                sensor.name,
                null,
                sensor.id,
                false
            )));
    }

    ngAfterViewInit() {
        this.zone.runOutsideAngular(async () => {
            this.chart = await this.chartService.createChart(this.CHART_DIV_ID);
            this.disposer = this.chartService.createChartZoomEvent(this.chart, this, false);
            this.analyticsDisposer = this.chartService.createChartAnalyticsEvent(this.chart, this);
            this.analyticsVerticalDisposer = this.chartService.createChartAnalyticsVerticalZoomEvent(this.chart, this);
        });
    }

    onInitialZoom(range) {
        if (this.initDisposer && !this.initDisposer.isDisposed() && range.start && range.end) {
            this.onDataZoom(range);
        }
    }

    onZoom(range) {
        if (range.start && range.end && this.initDisposer && !this.initDisposer.isDisposed()) {
            this.initDisposer.dispose();
        }
        this.onDataZoom(range);
    }

    onDataZoom(range) {
        this.zone.run(() => {
            const timestampStart = range.start;
            const timestampEnd = range.end;
            this.deviceValues = {};

            if (!timestampStart || !timestampEnd ||
                !this.chart.series || this.chart.series.length < 1) {
                return;
            }

            for (let i = 0; i < this.chart.series.length; i++) {
                const series = this.chart.series.getIndex(i);
                const name = (series.get('name') ?? '').split(' - ')[0];
                const paramField = series.get('valueYField') as string;
                const paramId = this.chartService.getParameterId(paramField);

                let deviceValue = this.deviceValues[name];
                if (!deviceValue) {
                    deviceValue = new DeviceValue();
                    this.deviceValues[name] = deviceValue;
                }

                deviceValue.average[paramId] = 0;
                deviceValue.peak[paramId] = 0;
                let count = 0;

                (series.data.values as SensorRecord[]).forEach((s: SensorRecord) => {
                    if (s.timestamp < timestampStart || s.timestamp > timestampEnd) {
                        return;
                    }

                    if (deviceValue.peak[paramId] < s[paramField]) {
                        deviceValue.peak[paramId] = s[paramField];
                    }
                    deviceValue.average[paramId] += s[paramField];
                    count++;
                });
                if (count > 0) {
                    deviceValue.average[paramId] /= count;
                }
            }

            const averages: Record<string, Record<number, number>> = {};
            const peaks: Record<string, Record<number, number>> = {};
            Object.keys(this.deviceValues).forEach(device => {
                const dv = this.deviceValues[device];
                if (dv) {
                    averages[device] = { ...dv.average };
                    peaks[device] = { ...dv.peak };
                }
            });
            this.deviceAverages = averages;
            this.devicePeaks = peaks;
        });
    }

    onAnalyticsHorizontalZoom() {
        if (!this.shouldRecordAnalyticsZoom) {
            this.shouldRecordAnalyticsZoom = true;
            return;
        }
    }

    onAnalyticsVerticalZoom() {
        if (!this.shouldRecordVerticalZoom) {
            this.shouldRecordVerticalZoom = true;
            return;
        }
    }

    ngOnDestroy() {
        this.zone.runOutsideAngular(() => {
            this.chartService.disposeEvent(this.disposer);
            this.chartService.disposeEvent(this.initDisposer);
            this.chartService.disposeEvent(this.analyticsDisposer);
            this.chartService.disposeEvent(this.analyticsVerticalDisposer);
            if (this.chart) {
                this.chartService.disposeChartData(this.chart);
                this.chart.dispose();
            }
        });
    }

    selectInterval(id: number) {
        if (id === TimeIntervalEnum.DailyShift) {
            const dialogRef = this.dialog.open(TimeRangeDialogComponent, {
                data: this.shiftInterval,
                width: '450px',
                disableClose: true
            });

            dialogRef.afterClosed()
                .subscribe((dateRange: DateRange) => {
                    if (dateRange) {
                        this.shiftInterval = dateRange;
                    } else {
                        this.timeIntervalId = undefined;
                    }
                });
        }
    }

    search() {
        if (this.isDisabled() ||
            !this.chartService.validateDateRange(this.fromDate, this.toDate)) {
            return;
        }

        this.isLoading = true;
        const startDate = this.dateFormatService.formatDateTimeToString(this.fromDate, this.dateFormatService.dateToStringWithTimeFormat);
        const endDate = this.dateFormatService.formatDateTimeToString(this.toDate, this.dateFormatService.dateToStringWithTimeFormat);
        const startTime = this.dateFormatService.formatDateTimeToString(this.shiftInterval.startDate, this.dateFormatService.dateToStringWithTimeFormat);
        const endTime = this.dateFormatService.formatDateTimeToString(this.shiftInterval.endDate, this.dateFormatService.dateToStringWithTimeFormat);

        const selectedEndPointsAndSensors = this.getSelectedEndPointsAndSensors();

        const requests: Observable<SensorAnalysisRecord[]>[] = [];
        selectedEndPointsAndSensors.forEach(x => {
            const payload = new DataAnalysisRequestModel();
            if (x.isEndPoint) {
                payload.endPointId = x.endPointId;
            } else {
                payload.sensorId = x.sensorId;
            }
            payload.isViewByEndPoint = x.isEndPoint;
            payload.startDate = startDate;
            payload.endDate = endDate;
            payload.parameters = this.selectedParams;
            payload.timeIntervalId = this.timeIntervalId;
            payload.startTime = startTime;
            payload.endTime = endTime;
            payload.selectedDevices = this.selected;

            requests.push(this.sensorRecordService.getParameters(payload));
        });

        if (!requests) {
            this.isLoading = false;
            return;
        }

        forkJoin(requests)
            .subscribe(results => {
                this.updateChart(selectedEndPointsAndSensors.map(x => x.name), results);
                this.isLoading = false;
            },
                error => {
                    this.toastService.showError(error);
                    this.isLoading = false;
                }
            );
    }

    async updateChart(names: string[], data: SensorAnalysisRecord[][]) {
        const { am5: am5Ns } = await this.chartService.getAmCharts5();
        this.chartService.disposeChartData(this.chart);
        this.chartService.disposeEvent(this.initDisposer);
        this.initDisposer = this.zone.runOutsideAngular(() => {
            return this.chartService.createChartInitialZoomEvent(this.chart, this, false);
        });
        this.shouldRecordAnalyticsZoom = false;
        this.shouldRecordVerticalZoom = false;

        const title = this.chartService.getValueAxisTitle(this.selectedParams);
        const valueAxis = this.chart.yAxes.getIndex(0) as am5xy.ValueAxis<am5xy.AxisRendererY>;
        valueAxis.children.each((child) => {
            if (child instanceof am5Ns.Label) {
                child.set('text', title);
            }
        });

        this.rowDevices = JSON.parse(JSON.stringify(names));
        this.columnParameters = JSON.parse(JSON.stringify(this.selectedParams));

        const series = [];
        for (const [i, name] of names.entries()) {
            const sensorRecords = data[i];

            const values: any[][] = [];
            this.selectedParams.forEach(p => values.push([]));

            sensorRecords.forEach(record => {
                const timestamp = record.timestamp * 1000;
                this.selectedParams.forEach((p, j) => {
                    const value = new SensorRecord();
                    value.timestamp = timestamp;
                    const paramField = this.chartService.getParameterField(p);
                    value[paramField] = record[paramField];
                    values[j].push(value);
                });
            });

            for (const [j, p] of this.selectedParams.entries()) {
                const s = this.chart.series.push(
                    await this.chartService.createLineSeries(p, this.selectedParams.length === 1, name, j, null, this.chart));
                s.data.setAll(values[j]);
                series.push(s);
            }
        }

        await this.chartService.createXYChartScrollbar(this.chart, series);
        this.chart.set('scrollbarY', am5Ns.Scrollbar.new(this.chart.root, { orientation: 'vertical' }));
    }
    // noData: 'No data found' : TODO :

    export() {
        if (this.isDisabled() ||
            !this.chartService.validateDateRange(this.fromDate, this.toDate)) {
            return;
        }

        this.isLoading = true;
        const startDate = this.dateFormatService.formatDateTimeToString(this.fromDate, this.dateFormatService.dateToStringWithTimeFormat);
        const endDate = this.dateFormatService.formatDateTimeToString(this.toDate, this.dateFormatService.dateToStringWithTimeFormat);
        const startTime = this.dateFormatService.formatDateTimeToString(this.shiftInterval.startDate, this.dateFormatService.dateToStringWithTimeFormat);
        const endTime = this.dateFormatService.formatDateTimeToString(this.shiftInterval.endDate, this.dateFormatService.dateToStringWithTimeFormat);

        const selectedEndPointsAndSensors = this.getSelectedEndPointsAndSensors();
        const models: DataAnalysisRequestModel[] = [];
        selectedEndPointsAndSensors.forEach(x => {
            const model = new DataAnalysisRequestModel();
            if (x.isEndPoint) {
                model.endPointId = x.endPointId;
            } else {
                model.sensorId = x.sensorId;
            }
            model.name = x.isEndPoint ? this.endPoints.find(m => m.id === x.id)?.name : this.sensors.find(s => s.id === x.id)?.name;
            model.isViewByEndPoint = x.isEndPoint;
            model.startDate = startDate;
            model.endDate = endDate;
            model.parameters = this.selectedParams;
            model.timeIntervalId = this.timeIntervalId;
            model.startTime = startTime;
            model.endTime = endTime;
            model.selectedDevices = this.selected;

            models.push(model);
        });

        if (!models) {
            this.isLoading = false;
            return;
        }

        const payload = new DataAnalysisExportModel();
        payload.models = models;

        this.sensorRecordService.exportParameters(payload)
            .subscribe(
                blob => {
                    this.chartService.downloadFile(blob);
                    this.isLoading = false;
                },
                error => {
                    this.toastService.showError(error);
                    this.isLoading = false;
                });

    }

    isDisabled() {
        return !this.selected || this.selected.length < 1
            || !this.fromDate || !this.toDate
            || !this.selectedParams || this.selectedParams.length < 1
            || this.timeIntervalId === undefined;
    }

    getSelectedEndPointsAndSensors(): EndPointSensorLookup[] {
        const selectedEndPointsAndSensors: EndPointSensorLookup[] = [];
        this.selected.forEach(lookupId => {
            const selectedLookup = lookupId.startsWith('0+')
                ? this.endPoints.find(x => x.id === lookupId)
                : this.sensors.find(x => x.id === lookupId);
            if (selectedLookup) {
                selectedEndPointsAndSensors.push(selectedLookup);
            }
        });

        return selectedEndPointsAndSensors;
    }

    getParamName(paramId: number) {
        return this.chartService.getParameterName(paramId);
    }

    getParamUnit(paramId: number) {
        return this.valueConversionService.getParameterUnit(paramId);
    }

    getValue(id: number, device: string, paramId: number) {
        if (!this.deviceValues[device]) {
            return -1;
        }

        return id === 0
            ? this.deviceValues[device].average[paramId]
            : this.deviceValues[device].peak[paramId];
    }

    onResized(event: ResizedEvent) {
        this.chartService.onResized(this.chart, event, this.CHART_DIV_ID);
    }

    public trackByIndex = trackByIndex;
}
